import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { VERTICAL_META, getVerticalMeta, IconCustom } from "./VerticalIcons";

interface VerticalPickerDialogProps {
  value?: string;
  onChange: (value: string) => void;
  /** Render prop: the trigger element */
  children: React.ReactNode;
}

const PRESET_KEYS = Object.keys(VERTICAL_META);

export function VerticalPickerDialog({ value, onChange, children }: VerticalPickerDialogProps) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState("");

  const isCustomValue = value && !PRESET_KEYS.includes(value);

  function select(key: string) {
    onChange(key);
    setCustomMode(false);
    setOpen(false);
  }

  function confirmCustom() {
    const trimmed = customInput.trim().toUpperCase().replace(/\s+/g, "_");
    if (trimmed) {
      onChange(trimmed);
      setOpen(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-divider">
          <DialogTitle className="text-base font-semibold">Select Vertical</DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Choose the domain your agent specialises in, or describe your own.</p>
        </DialogHeader>

        <div className="p-5">
          {/* Preset grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PRESET_KEYS.map((key) => {
              const { label, Icon, color, bg } = VERTICAL_META[key];
              const active = value === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => select(key)}
                  className={cn(
                    "group relative flex items-center gap-3 rounded-xl px-3 py-3 border text-left transition-all duration-150",
                    active
                      ? "border-primary/60 bg-primary/8 shadow-[0_0_0_2px_hsl(var(--primary)/0.2)]"
                      : "border-divider bg-surface-subtle hover:border-border hover:bg-surface-subtle"
                  )}
                >
                  <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", bg)}>
                    <Icon size={18} className={color} />
                  </div>
                  <span className={cn("text-sm font-medium leading-tight", active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground")}>
                    {label}
                  </span>
                  {active && (
                    <Check className="absolute top-2 right-2 h-3.5 w-3.5 text-primary" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-divider" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or describe your own</span>
            <div className="flex-1 h-px bg-divider" />
          </div>

          {/* Custom input row */}
          {customMode || isCustomValue ? (
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder="e.g. Video Editing, Legal Research..."
                className="bg-background/50 font-mono text-sm flex-1"
                defaultValue={isCustomValue && !customMode ? value : customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmCustom();
                  if (e.key === "Escape") { setCustomMode(false); setCustomInput(""); }
                }}
              />
              <Button type="button" size="sm" onClick={confirmCustom} disabled={!customInput.trim()}>
                Use this
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setCustomMode(false); setCustomInput(""); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCustomMode(true)}
              className={cn(
                "w-full flex items-center gap-3 rounded-xl px-3 py-3 border border-dashed border-divider hover:border-border transition-colors text-left",
                isCustomValue && "border-primary/40 bg-primary/5"
              )}
            >
              <div className="h-9 w-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
                <Pencil className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground hover:text-foreground">
                  {isCustomValue ? value : "Type a custom vertical"}
                </p>
                <p className="text-[10px] text-muted-foreground/60">Any skill or domain you can describe</p>
              </div>
              {isCustomValue && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Compact badge showing the currently selected vertical — use as the dialog trigger */
export function VerticalBadge({ value, placeholder = "Select vertical" }: { value?: string; placeholder?: string }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-divider text-muted-foreground text-sm w-full hover:border-border transition-colors cursor-pointer">
        <div className="h-7 w-7 rounded-lg bg-muted/30 flex items-center justify-center">
          <IconCustom size={14} className="text-muted-foreground" />
        </div>
        <span>{placeholder}</span>
      </div>
    );
  }

  const { label, Icon, color, bg } = getVerticalMeta(value);
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-divider bg-surface-subtle text-sm w-full hover:border-border transition-colors cursor-pointer">
      <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0", bg)}>
        <Icon size={14} className={color} />
      </div>
      <span className="font-medium">{label}</span>
    </div>
  );
}
